import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ADMIN } from "@/constants/testIds";
import { Lock } from "lucide-react";

// Admin sign-in. On successful password auth, if the account lacks the admin
// claim we try a one-time self-bootstrap (allowlisted emails only) before
// granting access.
export default function AdminLogin() {
  const { user, isAdmin, loading, signIn, bootstrapAdmin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Already authorized → go to dashboard.
  useEffect(() => {
    if (!loading && user && isAdmin) navigate("/admin/dashboard", { replace: true });
  }, [loading, user, isAdmin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await signIn(email.trim(), password);
      // If the account already carries the admin claim (set server-side), enter
      // directly — no Cloud Function needed.
      const tok = await u.getIdTokenResult();
      if (tok.claims.admin === true) {
        navigate("/admin/dashboard", { replace: true });
        return;
      }
      // Otherwise attempt a one-time self-bootstrap (allowlisted emails only;
      // requires the bootstrapAdmin function to be deployed).
      try {
        await bootstrapAdmin();
        navigate("/admin/dashboard", { replace: true });
      } catch (be) {
        if (String(be?.code || be?.message).includes("permission-denied")) {
          setError("This account is not authorized for admin access.");
        } else {
          setError("Admin access isn’t provisioned for this account yet.");
        }
        setBusy(false);
      }
    } catch (err) {
      const code = err?.code || "";
      if (code.includes("auth/")) setError("Incorrect email or password.");
      else setError("Sign-in failed. Please try again.");
      // eslint-disable-next-line no-console
      console.error("[admin-login]", err);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md border border-bronze/30 bg-cream p-10">
        <div className="flex items-center gap-3 text-bronze">
          <Lock size={18} />
          <span className="text-[0.7rem] tracking-[0.28em] uppercase">A Homely Abode · Admin</span>
        </div>
        <h1 className="font-serif text-3xl text-ink mt-4">Sign in</h1>
        <p className="text-sm text-olive mt-1">Manage listings, leads, content and SEO.</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <label className="block">
            <span className="editorial-label">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={ADMIN.loginEmail}
              className="editorial-input"
              autoComplete="username"
              required
            />
          </label>
          <label className="block">
            <span className="editorial-label">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={ADMIN.loginPassword}
              className="editorial-input"
              autoComplete="current-password"
              required
            />
          </label>

          {error && (
            <p data-testid={ADMIN.loginError} className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">
              {error}
            </p>
          )}

          <button type="submit" disabled={busy} data-testid={ADMIN.loginSubmit} className="btn-primary w-full disabled:opacity-60">
            {busy ? "Signing in…" : "Sign In"}
          </button>

          <Link
            to="/admin/forgot"
            data-testid={ADMIN.loginForgotLink}
            className="block text-center text-sm text-olive hover:text-bronze"
          >
            Forgot password?
          </Link>
        </form>
      </div>
    </div>
  );
}
